import React, { useEffect, useState } from "react";
import axios from "axios";
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from "react-router";
import { Toaster } from "./components/Toaster/Toaster";
import { toast } from "./components/Toaster/toast";
import "./assets/fonts/fonts.css";
import "./App.css";
import { setupAxiosInterceptor, setupFetchInterceptor } from "./utils/axiosInterceptor";
import { redirectToLogin } from "./utils/returnHash";
import warmPrismComponents from "./utils/warmPrismComponents";

import Loader from "./components/Loader";
import Header from "./components/Header";
import ApplicationContainer from "./components/Application/ApplicationContainer";
import SubmittedApp from "./components/Application/SubmittedApp";
import Rater from "./components/Application/Rater";
import Home from "./components/Home";
import ManagerApproval from "./components/Application/ManagerApproval";
import Pledge from "./components/Pledge";
import { ProjectFair, Projects } from "./components/Projects";
import CommitteeBios from "./components/Committee";
import Dashboard from "./components/Dashboard";
import Project from "./components/Project";
import AdminSettings from "./components/AdminSettings";
import { ProfileForm } from "./components/Profile";
import NotFound from "./components/NotFound";
import LoginFailure from "./components/LoginFailure";
import Relationships from './components/Relationships/Relationships';
import ManagerApprovalPage from './components/ManagerApprovalPage';
import ErrorBoundary from "./components/ErrorBoundary";
import PageViewTracker from "./components/PageViewTracker";

const routes = {
  "/about": CommitteeBios,
  "/projects": Projects,
  "/projects/:project_id/edit": Project,
  "/project-fair/:year": ProjectFair,
  "/apply": ApplicationContainer,
  "/admin-settings/*": AdminSettings,
  "/application/:application_id": SubmittedApp,
  "/application/:application_id/manager_approval": ManagerApproval,
  "/application-rater": Rater,
  "/graduation-pledge": Pledge,
  "/dashboard/:event_id?": Dashboard,
  "/manager-approval": ManagerApprovalPage,
  "/profile/:user_id/edit": ProfileForm,
  "/": Home,
  "/relationships": Relationships,
};

// Pages predate the router hooks and take their URL params as a `params` prop.
const RoutedPage = ({ Page, ...props }) => <Page params={useParams()} {...props} />;

const App = () => {
  const [loading, setLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState(undefined)

  // Setup global interceptors for authentication errors
  // This runs once when the app loads
  useEffect(() => {
    setupAxiosInterceptor();
    setupFetchInterceptor();
  }, []);

  // Prism fetches each component's chunk on first connect. The Dark Mode switch lives in the
  // user menu, which only renders while open, so without this the fetch starts on the user's
  // first menu click and the toggle paints late into an already-reserved row.
  useEffect(() => warmPrismComponents(), []);

  const refreshCurrentUser = () => {
    return axios.get("/auth/current_user")
      .then((result) => {
        setCurrentUser(result.data)
        return result.data
      })
      // Not a toast: this also runs on first mount, where a signed-out visitor
      // legitimately has no current user. Logging keeps the failure visible
      // without shouting at an anonymous page load.
      .catch((error) => {
        console.error('Failed to refresh the current user', error)
        return null
      })
  }

  const applyTheme = (theme) => {
    const html = document.documentElement;
    if (theme === 'dark') {
      html.setAttribute('data-theme', 'dark');
      html.classList.add('dark');
    } else {
      html.removeAttribute('data-theme');
      html.classList.remove('dark');
    }
  };

  // Apply theme whenever currentUser changes (covers login, login-as, end-login-as)
  useEffect(() => {
    applyTheme(currentUser?.User?.theme || 'light');
  }, [currentUser]);

  const handleThemeChange = async (newTheme) => {
    try {
      await axios.patch(`/api/users/${currentUser.user_id}/theme`, { theme: newTheme });
      setCurrentUser(prev => ({
        ...prev,
        User: { ...prev.User, theme: newTheme }
      }));
      applyTheme(newTheme);
    } catch {
      toast.error('Failed to save theme preference.');
    }
  };

  // Impersonation used to fail silently in both directions: the POST returned 200
  // even for a target the middleware could never swap in, and neither the request
  // nor the refresh had a catch. The screen reloaded as the original user with no
  // explanation. Both ends now report.
  const loginAsUser = (id) => {
    const ending = !id || id === 'none'
    setLoading(true)
    return axios.post(`/api/users/simulate/${id || 'none'}`)
    .then(()=>refreshCurrentUser())
    .then((user)=>{
      if (!user) return
      if (!ending && !user.simulated) {
        toast.error('Impersonation did not take effect for that account. Please report this.')
      }
    })
    .catch((error)=>{
      const message = error?.response?.data?.message
      toast.error(message || (ending
        ? 'Could not end impersonation. Please try again.'
        : 'Could not impersonate that user. Please try again.'))
    })
    .finally(()=>setLoading(false))
  }

  const endLoginAs = () => loginAsUser('none')

  useEffect(()=>{
    refreshCurrentUser()
    .finally(()=>setLoading(false))
  },[])

  if (loading) return <Loader/>
  if (window.location.pathname == '/login-failure') return <LoginFailure/>
  // Stash the fragment before leaving: the server derives its post-login
  // redirect from `Referer`, which browsers strip the fragment from, so a deep
  // link like /project-fair/2026#west would otherwise return as /project-fair/2026.
  // Return after redirecting: the browser keeps running this render until the
  // navigation lands, and mounting the routes here fired the page's API and
  // image requests with no session (hundreds of 401s during the 2026 Fair).
  if (!currentUser) {
    redirectToLogin()
    return <Loader/>
  }

  return (
    <ErrorBoundary>
      <div className="App">
        <Router>
          <PageViewTracker/>
          <Header currentUser={currentUser} loginAsUser={loginAsUser} endLoginAs={endLoginAs} onThemeChange={handleThemeChange}/>
          <Toaster />
          <div className="page-content">
            <Routes>
              {Object.entries(routes).map(([route, Page]) => (
                <Route
                  key={route}
                  path={route}
                  element={
                    <RoutedPage
                      Page={Page}
                      currentUser={currentUser}
                      loginAsUser={loginAsUser}
                      endLoginAs={endLoginAs}
                      refreshCurrentUser={refreshCurrentUser}
                    />
                  }
                />
              ))}
              <Route path="/pledge/*" element={<Navigate to="/graduation-pledge" replace />} />
              <Route path="/pledges/*" element={<Navigate to="/admin-settings/graduation-pledges" replace />} />
              {/* The legacy Applications page now lives in Admin Settings → Application Rater. */}
              <Route path="/applications" element={<Navigate to="/admin-settings/application-rater" replace />} />
              <Route path="*" element={<NotFound/>} />
            </Routes>
          </div>
        </Router>
      </div>
    </ErrorBoundary>
  )
}

export default App;